import Alpine from 'alpinejs';
import ajax from '@imacrayon/alpine-ajax';
import collapse from '@alpinejs/collapse';
import focus from '@alpinejs/focus';
import resize from '@alpinejs/resize';
import htmx from 'htmx.org';

// Alpine plugins
Alpine.plugin(ajax);
Alpine.plugin(focus);
Alpine.plugin(resize);
Alpine.plugin(collapse);

window.Alpine = Alpine;
window.htmx = htmx;

/**
 * Back to top.
 *
 * ⚠ The threshold comparison lives here rather than inline in the template: a
 * `>` inside an Alpine attribute ends the tag early, swallowing the next
 * element into the attribute value. Any Alpine expression needing < or >
 * belongs in a script.
 */
document.addEventListener('alpine:init', () => {
    Alpine.data('backToTop', (threshold = 400) => ({
        shown: false,

        init() {
            this.update();
        },

        update() {
            this.shown = window.scrollY > threshold;
        },
    }));
});

// WordPress nonce for Alpine AJAX
document.addEventListener('alpine:init', () => {
    const wpNonce = window.wpApiSettings?.nonce || window.nonce;
    
    if (wpNonce) {
        Alpine.store('ajax', {
            headers: {
                'X-WP-Nonce': wpNonce,
                'Content-Type': 'application/json',
                'Accept': 'application/json'
            }
        });
    }
});

// ...and for HTMX
const wpNonce = window.wpApiSettings?.nonce || window.nonce;
if(wpNonce) {
    document.addEventListener('htmx:configRequest', function(event) {
        event.detail.headers['X-WP-Nonce'] = wpNonce;
    });
}

/**
 * Publish the header's height as --og--header-height, for anything sizing
 * itself against "the rest of the screen".
 *
 * A ResizeObserver rather than a resize listener: the header changes height for
 * reasons the window does not know about, the nav collapsing on its own content
 * measurement chief among them.
 */
document.addEventListener('DOMContentLoaded', () => {
    const header = document.querySelector('header');
    if (!header) return;

    const publish = () =>
        document.documentElement.style.setProperty('--og--header-height', `${header.offsetHeight}px`);

    publish();
    new ResizeObserver(publish).observe(header);
});

/**
 * Make an overflowing table reachable by keyboard (WCAG 2.1.1) — the
 * `overflow-x: auto` in components.css solves it for a pointer only, since a
 * scroll container is not focusable by default.
 *
 * ⚠ tabindex is added only while the table actually overflows; marking every
 * table focusable adds tab stops a keyboard user has to page through.
 *
 * role="region" plus a name is what makes the stop meaningful.
 */
document.addEventListener('DOMContentLoaded', () => {
    const figures = document.querySelectorAll('.wp-block-table');
    if (!figures.length) return;

    const sync = (figure) => {
        /* +1 absorbs sub-pixel rounding, which otherwise marks every table
           scrollable. */
        const overflows = figure.scrollWidth > figure.clientWidth + 1;

        if (overflows) {
            figure.setAttribute('tabindex', '0');
            figure.setAttribute('role', 'region');

            if (!figure.hasAttribute('aria-label')) {
                const caption = figure.querySelector('figcaption')?.textContent?.trim();
                figure.setAttribute('aria-label', caption || 'Table, scrollable');
            }
        } else {
            figure.removeAttribute('tabindex');
            figure.removeAttribute('role');
        }
    };

    figures.forEach((figure) => {
        sync(figure);
        /* The table can cross the threshold with no window resize at all — a
           sidebar appearing, a font loading. */
        new ResizeObserver(() => sync(figure)).observe(figure);
    });
});

document.addEventListener('DOMContentLoaded', function() {
    Alpine.start();
});
